import type { GameResultVariant } from '@/types/result-content';
import { appConfig } from '@app/config/appConfig';

export const MAZE_COLS = 15;
export const MAZE_ROWS = 10;
export const MAZE_CELL_SIZE = 40;
/** After a junction is reached (and live moves flushed), wait this long before drawing hint arrows; skip if the player moves sooner. */
export const MAZE_JUNCTION_ARROW_REVEAL_DELAY_MS = 420;
/** Minimum ms between accepted movement inputs (idle, junction, and mid-roll redirects). Live move rows are queued per completed grid step, not throttled by this value. */
export const MAZE_MOVE_INPUT_COOLDOWN_MS = 150;
/** After the player stops moving, flush queued live moves if still idle this long. */
export const MAZE_SUBMIT_MOVES_IDLE_FLUSH_MS = 600;
/** Flush immediately when stopped at a junction with at least this many choices (e.g. T or cross). */
export const MAZE_SUBMIT_MOVES_MIN_JUNCTION_EXITS = 3;
export const MAZE_WALL_THICKNESS = 6;
/** Per-side inset for wall cubes; 0 keeps adjacent cubes flush (no seam). */
export const MAZE_WALL_CUBE_INSET_PX = 0;
/** Visual extrusion depth for pseudo-3D wall rendering (px). */
export const MAZE_WALL_DEPTH = 4;
export const MAZE_BALL_SIZE = 18;
export const MAZE_START_TIMER = 120;
/** Live HUD: blink timer chip when remaining time is at or below this (seconds). */
export const MAZE_TIMER_LOW_WARNING_SECONDS = 5;
/** Demo: show completion countdown before leaving the play route. */
export const MAZE_DEMO_COMPLETE_EXIT_DELAY_MS = 5000;
export const MAZE_LEVEL_COMPLETE_BASE_SCORE = 50;
export const MAZE_TIMER_SCORE_FACTOR = 0.5;

/** `GET /game/status` and session payloads — matches backend `GAME_SESSION_STATUS`. */
export const GAME_SESSION_STATUS = {
  STARTED: 1,
  ENDED: 2,
  EXPIRED: 3,
  RESULT_PROCESSING: 4,
  MANUALLY_ENDED: 5,
} as const;

export type GameSessionStatusValue = (typeof GAME_SESSION_STATUS)[keyof typeof GAME_SESSION_STATUS];

/** Live bootstrap: outcome of `runLiveReachedEndRecoveryIfNeeded`. */
export const LIVE_REACHED_END_RECOVERY_RESULT = {
  NOT_NEEDED: 'not-needed',
  HANDLED: 'handled',
} as const;

export type LiveReachedEndRecoveryResult =
  (typeof LIVE_REACHED_END_RECOVERY_RESULT)[keyof typeof LIVE_REACHED_END_RECOVERY_RESULT];

/** Level-complete card flip animation phase (HUD / canvas input gating). */
export const MAZE_FLIP_PHASE = {
  IDLE: 'idle',
  FOLDING: 'folding',
  UNFOLDING: 'unfolding',
} as const;

export type MazeFlipPhase = (typeof MAZE_FLIP_PHASE)[keyof typeof MAZE_FLIP_PHASE];

/** Admin stage-content API: game type filter (env-backed `appConfig.stage.contentGameType`). */
export const STAGE_CONTENT_GAME_TYPE = appConfig.stage.contentGameType;

/** Result screen / live session terminal outcome (matches `GameResultVariant`). */
export const GAME_RESULT_VARIANT = {
  SUCCESS: 'success',
  FAILURE: 'failure',
} as const satisfies Record<'SUCCESS' | 'FAILURE', GameResultVariant>;
